// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Form, MapType, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useNavigationDOM, useState} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';

// App
import ContextSwitcher from '@shared/design/components/ContextSwitcher';
import ActionPanel from '@shared/design/components/Panel/ActionPanel';
import JobForm from '@shared/modules/Job/forms/JobForm';
import SalespersonCalendar from '@shared/modules/Project/components/SalespersonCalendar';
import ResponsiveTextInput from 'modules/App/components/ResponsiveTextInput';
import JobMap from 'modules/Job/components/JobMap';

const MIN_HEIGHT = 576;

const HeaderContainer = Styled.View`
`;

const Container = Styled.View`
  flex: 1;
`;

const NotesInput = ({
  form,
  notesField,
  autoFocus,
  label,
  placeholder,
  isDisabled,
}: {
  form: Form;
  notesField: string;
  autoFocus?: boolean;
  label: string;
  placeholder: string;
  isDisabled: boolean;
}) => {
  return (
    <ResponsiveTextInput.Memoized
      form={form}
      field={notesField}
      autoFocus={autoFocus}
      label={label}
      minHeight={96}
      maxHeight={320}
      input={{placeholder, disabled: isDisabled}}
      isResponsive
    />
  );
};

const EditJobNotes = ({
  form,
  field,
  isDisabled,
}: {
  form: Form;
  field: string;
  isDisabled: boolean;
}) => {
  const {params} = useNavigationDOM();

  return (
    <React.Fragment>
      <NotesInput
        form={form}
        notesField={`${field}.officeNotes`}
        autoFocus={params.field === 'officeNotes'}
        label={'Office Notes'}
        placeholder={'Enter notes for the office'}
        isDisabled={isDisabled}
      />
      <Space height={16} />
      <NotesInput
        form={form}
        notesField={`${field}.dispatchNotes`}
        autoFocus={params.field === 'dispatchNotes'}
        label={'Dispatch Notes'}
        placeholder={'Enter notes for dispatch'}
        isDisabled={isDisabled}
      />
      <Space height={16} />
      <NotesInput
        form={form}
        notesField={`${field}.additionalNotes`}
        label={'Crew Notes'}
        placeholder={'Enter notes for crew'}
        isDisabled={isDisabled}
      />
    </React.Fragment>
  );
};

const getWarehouseLocationFromForm = ({form, field}: {form: Form; field: string}) => {
  const warehouseLocationFromForm = _.get(form.values, `${field}.warehouseLocationForm`);
  return warehouseLocationFromForm
    ? {
        latitude: warehouseLocationFromForm.latitude,
        longitude: warehouseLocationFromForm.longitude,
      }
    : null;
};

const RouteMap = ({
  form,
  field,
  project,
  mapRef,
}: {
  form: Form;
  field: string;
  project: ProjectModel;
  mapRef: React.RefObject<MapType>;
}) => {
  const {isEnabledCreateStorageMultipleWarehouse} = project.organization.features;

  const warehouseLocation = !isEnabledCreateStorageMultipleWarehouse
    ? project.organization.warehouseLocation
    : getWarehouseLocationFromForm({form, field});

  const moveLocations = JobForm.getLocations(_.get(form.values, field));
  const boundLocations = warehouseLocation ? [warehouseLocation, ...moveLocations] : moveLocations;

  return (
    <JobMap
      locations={moveLocations}
      boundLocations={boundLocations}
      warehouseLocation={warehouseLocation}
      mapRef={mapRef}
    />
  );
};

const CONTEXT_OPTIONS = [
  {
    label: 'Notes',
    key: 'NOTES',
  },
  {
    label: 'Map',
    key: 'MAP',
  },
  {
    label: 'Sales Calendar',
    key: 'SALES_CALENDAR',
  },
];

const NotesAndRouteAndCalendarBlock = ({
  form,
  field,
  project,
  mapRef,
}: {
  form: Form;
  field: string;
  project: ProjectModel;
  mapRef: React.RefObject<any>;
}) => {
  const {navigator} = useNavigationDOM();
  const [selectedContext, setSelectedContext] = useState(CONTEXT_OPTIONS[0].key);

  const {bookedById, additionalSalespersonIds} = project;

  const selectedSalesPersonIds = [bookedById, ...additionalSalespersonIds].filter((id) => !!id);

  const jobForm = _.get(form.values, field);

  return (
    <Container>
      <HeaderContainer>
        <ContextSwitcher
          isFullWidth
          contextDefinitions={CONTEXT_OPTIONS.map(({key, ...props}) => {
            const isSelected = selectedContext === key;
            return {
              ...props,
              isSelected,
              isDisabled: isSelected,
              onPress: () => setSelectedContext(key),
            };
          })}
        />
      </HeaderContainer>
      <Space height={16} />
      <Container style={{borderRadius: 8, overflow: 'scroll'}}>
        {selectedContext === 'NOTES' && (
          <EditJobNotes form={form} field={field} isDisabled={false} />
        )}
        {selectedContext === 'MAP' && (
          <RouteMap form={form} field={field} project={project} mapRef={mapRef} />
        )}
        {selectedContext === 'SALES_CALENDAR' && (
          <SalespersonCalendar
            initialSelectedSalesPersonIds={selectedSalesPersonIds}
            organizationSlug={project.organization.slug}
            jobDate={jobForm?.date}
            handleSetUpSalesCalendar={() => navigator.push('/settings/inventory/general')}
          />
        )}
      </Container>
    </Container>
  );
};

const EditJobNotesAndRouteAndCalendarBlock = ({
  form,
  field,
  project,
  mapRef,
}: {
  form: Form;
  field: string;
  project: ProjectModel;
  mapRef: React.RefObject<any>;
}) => {
  return (
    <ActionPanel
      BodyComponent={NotesAndRouteAndCalendarBlock}
      bodyComponentProps={{form, field, project, mapRef}}
      HeaderComponent={() => null}
      title={'Route'}
      style={{width: '100%', flex: 1}}
      bodyStyle={{display: 'flex', flex: 1}}
    />
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
EditJobNotesAndRouteAndCalendarBlock.fragment = gql`
  fragment EditJobNotesAndRouteAndCalendarBlock on Project {
    id
    bookedById
    additionalSalespersonIds
    organization {
      id
      slug
      warehouseLocation {
        latitude
        longitude
      }
      features {
        isEnabledCreateStorageMultipleWarehouse: isEnabled(
          feature: "CREATE_STORAGE_MULTIPLE_WAREHOUSE"
        )
      }
    }
  }
`;

export default EditJobNotesAndRouteAndCalendarBlock;
